/**
 * Admin API Exports
 *
 * Typed API endpoints for admin panel PostgreSQL migration.
 * Paths match Firebase Cloud Function routes:
 *   /{functionName}/{route}
 *
 * Function names (from index.ts exports):
 *   product, deal, user, account, notifications, sector,
 *   category, chat, invoice, adminuser, finance
 */

import {
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiGetBlob,
  type ApiRequestOptions
} from './client'
import {encodeInvoiceId} from './invoiceId'
import {openApiStream, type ApiStreamHandlers} from './stream'

export * from './client'
export {usePostgreSQL} from './featureFlag'

export type StorageUploadIntent = {
  intentId: string
  uploadUrl: string
  headers: Record<string, string>
  quarantinePath: string
  filePath: string
  expiresInSeconds: number
}

export type StorageUploadResult = {
  filePath: string
  bucket: string
  url: string
  visibility: 'public' | 'private'
}

export type StorageObjectInfo = {
  filePath: string
  bucket: string
  url: string
  name: string
  originalName: string
  contentType: string
  size: number
}

export const storageApi = {
  createUploadIntent: (data: {
    filePath: string
    contentType: string
    size: number
    originalName: string
  }) => apiPost<StorageUploadIntent>('/storage/upload-intent', data),
  completeUpload: (data: {quarantinePath: string; filePath: string}) =>
    apiPost<StorageUploadResult>('/storage/upload-complete', data),
  getProductObjectInfo: (productId: string, filePath: string) =>
    apiGet<StorageObjectInfo>(
      `/storage/products/${encodeURIComponent(
        productId
      )}/object?path=${encodeURIComponent(filePath)}`
    ),
  getAdminAssetObjectInfo: (filePath: string) =>
    apiGet<StorageObjectInfo>(
      `/storage/admin-assets/object?path=${encodeURIComponent(filePath)}`
    ),
  deleteObject: (filePath: string) =>
    apiPost<{filePath: string; deleted: number}>('/storage/object/delete', {
      filePath
    })
}

// Account API — function: "account"
export const accountsApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/account/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/account${query}`)
  },
  create: <T = unknown>(data: unknown) => apiPost<T>('/account/create', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/account/update/${id}`, data),
  delete: (id: string) => apiDelete(`/account/delete/${id}`),
  deletePreview: <T = unknown>(id: string) =>
    apiGet<T>(`/account/delete-preview/${id}`)
}

// Contact/User API — function: "user"
export const contactsApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/user/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/user${query}`)
  },
  create: <T = unknown>(data: unknown) => apiPost<T>('/user/create', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/user/update/${id}`, data),
  delete: (id: string) => apiDelete(`/user/delete/${id}`),
  deletePreview: <T = unknown>(id: string) =>
    apiGet<T>(`/user/delete-preview/${id}`),
  refreshExternal: <T = unknown>(id: string) =>
    apiPost<T>(`/user/external-refresh/${id}`, {}),
  getAuthData: <T = unknown>(id: string) => apiGet<T>(`/user/auth-data/${id}`),
  sendVerification: <T = unknown>(id: string) =>
    apiPost<T>(`/user/verifyEmail/${id}`, {}),
  sendPasswordResetLink: <T = unknown>(data: unknown) =>
    apiPost<T>('/user/sendPasswordResetLink', data),
  updateStatus: <T = unknown>(id: string, isActive: boolean) =>
    apiPost<T>('/user/update-status', {contactId: id, isActive})
}

// Product API — function: "product"
export const productsApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/product/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/product${query}`)
  },
  listManaged: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/product/managed${query}`)
  },
  create: <T = unknown>(data: unknown) => apiPost<T>('/product/create', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/product/update/${id}`, data),
  processMedia: <T = unknown>(id: string, data: unknown) =>
    apiPost<T>(`/product/${id}/media/process`, data),
  removeMedia: <T = unknown>(id: string, data: unknown) =>
    apiPost<T>(`/product/${id}/media/remove`, data),
  delete: (id: string) => apiDelete(`/product/delete/${id}`),
  deletePreview: <T = unknown>(id: string) =>
    apiGet<T>(`/product/delete-preview/${id}`),
  approve: <T = unknown>(id: string) =>
    apiPatch<T>(`/product/update/${id}`, {status: 'approved'}),
  reject: <T = unknown>(id: string, reason: string) =>
    apiPatch<T>(`/product/update/${id}`, {status: 'rejected', reason}),
  disableBuyNow: <T = unknown>(id: string) =>
    apiPost<T>(`/product/disable-buy-now/${id}`, {}),
  updatePrice: <T = unknown>(id: string, priceData: unknown) =>
    apiPatch<T>(`/product/update-product-price/${id}`, priceData),
  getAuctionRuns: <T = unknown>(id: string) =>
    apiGet<T>(`/product/${id}/auction-runs`)
}

// Deal API — function: "deal"
export const dealsApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/deal/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/deal${query}`)
  },
  create: <T = unknown>(data: unknown) => apiPost<T>('/deal/create', data),
  archive: <T = unknown>(id: string, shouldArchive = true) =>
    apiPatch<T>(`/deal/${id}/archive`, {shouldArchive}),
  updateStage: <T = unknown>(id: string, stage: string) =>
    apiPatch<T>(`/deal/update/${id}`, {stage}),
  submitOffer: <T = unknown>(id: string, data: unknown) =>
    apiPost<T>(`/deal/${id}/offers`, data),
  editOffer: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/deal/${id}/offers/latest`, data),
  updateOfferStatus: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/deal/${id}/offers/latest/status`, data),
  requestInspection: <T = unknown>(id: string, data: unknown) =>
    apiPost<T>(`/deal/${id}/inspections`, data),
  editInspection: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/deal/${id}/inspections/latest`, data),
  updateInspectionStatus: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/deal/${id}/inspections/latest/status`, data)
}

// Invoice API — function: "invoice"
export const invoicesApi = {
  get: <T = unknown>(id: string) =>
    apiGet<T>(`/invoice/${encodeInvoiceId(id)}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/invoice${query}`)
  },
  generate: <T = unknown>(dealId: string) =>
    apiPost<T>(`/invoice/generate/${encodeURIComponent(dealId)}`, {}),
  addBuyerWire: <T = unknown>(invoiceId: string, wireData: unknown) =>
    apiPost<T>(
      `/invoice/add-buyer-wire/${encodeInvoiceId(invoiceId)}`,
      wireData
    ),
  addSellerWire: <T = unknown>(invoiceId: string, wireData: unknown) =>
    apiPost<T>(
      `/invoice/add-seller-wire/${encodeInvoiceId(invoiceId)}`,
      wireData
    ),
  addLineItems: <T = unknown>(invoiceId: string, data: unknown) =>
    apiPost<T>(`/invoice/line-items/${encodeInvoiceId(invoiceId)}`, data),
  removeInvoiceItem: <T = unknown>(invoiceId: string, data: unknown) =>
    apiPost<T>(
      `/invoice/remove-invoice-item/${encodeInvoiceId(invoiceId)}`,
      data
    ),
  markBuyerWireAsPaid: <T = unknown>(invoiceId: string, data: unknown) =>
    apiPost<T>(
      `/invoice/mark-buyer-wire-as-paid/${encodeInvoiceId(invoiceId)}`,
      data
    ),
  markAsClosed: <T = unknown>(invoiceId: string) =>
    apiPost<T>(`/invoice/mark-as-closed/${encodeInvoiceId(invoiceId)}`, {}),
  reOpen: <T = unknown>(invoiceId: string) =>
    apiPost<T>(`/invoice/re-open-invoice/${encodeInvoiceId(invoiceId)}`, {}),
  addComment: <T = unknown>(invoiceId: string, data: unknown) =>
    apiPost<T>(`/invoice/add-comment/${encodeInvoiceId(invoiceId)}`, data),
  recordNoticeOfRelease: <T = unknown>(invoiceId: string) =>
    apiPost<T>(`/invoice/notice-of-release/${encodeInvoiceId(invoiceId)}`, {})
}

// Admin API — function: "adminuser"
export const adminsApi = {
  requestMfaRecovery: <T = unknown>(email: string) =>
    apiPost<T>('/adminuser/mfa-recovery/request', {email}),
  verifyMfaRecovery: <T = unknown>(token: string) =>
    apiPost<T>('/adminuser/mfa-recovery/verify', {token}),
  listPendingMfaRecoveries: <T = unknown>() =>
    apiGet<T>('/adminuser/mfa-recovery/requests/pending'),
  approveMfaRecovery: <T = unknown>(requestId: string) =>
    apiPost<T>(
      `/adminuser/mfa-recovery/requests/${encodeURIComponent(
        requestId
      )}/approve`,
      {}
    ),
  listPaymentOperationsForReview: <T = unknown>() =>
    apiGet<T>('/adminuser/payment-operations/review'),
  resolvePaymentOperationReview: <T = unknown>(
    operationId: string,
    data: {
      decision: 'confirm_completed' | 'confirm_failed' | 'confirm_canceled'
      resolutionNote: string
    }
  ) =>
    apiPost<T>(
      `/adminuser/payment-operations/${encodeURIComponent(
        operationId
      )}/resolve`,
      data
    ),
  get: <T = unknown>(id: string) => apiGet<T>(`/adminuser/${id}`),
  getCurrent: <T = unknown>() => apiGet<T>('/adminuser/me'),
  sendCurrentEmailVerification: <T = unknown>() =>
    apiPost<T>('/adminuser/me/email-verification', {}),
  setPushSubscription: <T = unknown>(subscription: unknown) =>
    apiPut<T>('/adminuser/me/push-subscription', subscription),
  clearPushSubscription: (endpoint?: string) =>
    apiDelete(
      endpoint
        ? `/adminuser/me/push-subscription?endpoint=${encodeURIComponent(
            endpoint
          )}`
        : '/adminuser/me/push-subscription'
    ),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/adminuser${query}`)
  },
  create: <T = unknown>(data: unknown) => apiPost<T>('/adminuser/create', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPatch<T>(`/adminuser/update/${id}`, data),
  delete: (id: string) => apiDelete(`/adminuser/delete/${id}`),
  updateStatus: <T = unknown>(id: string, isActive: boolean) =>
    isActive
      ? apiPatch<T>(`/adminuser/grant/access/${id}`, {})
      : apiPatch<T>(`/adminuser/deny/access/${id}`, {})
}

export const auditLogsApi = {
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/adminuser/audit-events${query}`)
  }
}

export const authAuditApi = {
  record: <T = unknown>(data: unknown) =>
    apiPost<T>('/validation/auth-event', data)
}

// Notification API — function: "notifications"
export const notificationsApi = {
  getPushConfig: <T = unknown>() => apiGet<T>('/notifications/push/config'),
  list: <T = unknown>(
    params?: Record<string, string>,
    options?: ApiRequestOptions
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/notifications${query}`, options)
  },
  listTemplates: <T = unknown>() => apiGet<T>('/notifications/templates'),
  getTemplate: <T = unknown>(code: string | number) =>
    apiGet<T>(`/notifications/templates/${encodeURIComponent(String(code))}`),
  updateTemplate: <T = unknown>(code: string | number, data: unknown) =>
    apiPatch<T>(
      `/notifications/templates/${encodeURIComponent(String(code))}`,
      data
    ),
  markAsRead: <T = unknown>(id: string) =>
    apiPatch<T>(`/notifications/${id}/read`, {}),
  markAllAsRead: <T = unknown>() =>
    apiPost<T>('/notifications/mark-all-read', {}),
  markViewed: <T = unknown>() => apiPost<T>('/notifications/mark-viewed', {}),
  getUnreadCount: <T = unknown>() => apiGet<T>('/notifications/unread-count'),
  stream: <T = unknown>(
    params: Record<string, string> | undefined,
    handlers: ApiStreamHandlers<T>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return openApiStream<T>(`/notifications/stream${query}`, handlers)
  }
}

export const contentPagesApi = {
  list: <T = unknown>() => apiGet<T>('/content-pages'),
  get: <T = unknown>(slug: string) =>
    apiGet<T>(`/content-pages/${encodeURIComponent(slug)}`),
  update: <T = unknown>(slug: string, data: unknown) =>
    apiPatch<T>(`/content-pages/${encodeURIComponent(slug)}`, data),
  publish: <T = unknown>(slug: string) =>
    apiPost<T>(`/content-pages/${encodeURIComponent(slug)}/publish`, {}),
  unpublish: <T = unknown>(slug: string) =>
    apiPost<T>(`/content-pages/${encodeURIComponent(slug)}/unpublish`, {}),
  revisions: <T = unknown>(slug: string) =>
    apiGet<T>(`/content-pages/${encodeURIComponent(slug)}/revisions`)
}

// Public Questions API — uses product function routes
export const publicQuestionsApi = {
  list: <T = unknown>(data: unknown) =>
    apiPost<T>('/product/public-questions/list', data),
  answer: <T = unknown>(
    productId: string,
    questionId: string,
    answer: string,
    adminName: string
  ) =>
    apiPatch<T>(`/product/answer/public-question/${productId}/${questionId}`, {
      answer,
      adminName
    }),
  delete: (productId: string, questionId: string) =>
    apiDelete(`/product/delete/public-questions/${productId}/${questionId}`)
}

// Finance Applications API — function: "finance"
export const financeApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/finance/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/finance${query}`)
  },
  updateStatus: <T = unknown>(
    contactId: string,
    financeId: string,
    status: string
  ) =>
    apiPatch<T>(`/user/finance/action/${contactId}`, {
      financeId,
      financingStatus: status
    })
}

// Sales Tax API — function: "tax"
export const salesTaxApi = {
  get: <T = unknown>(id: string) => apiGet<T>(`/tax/sales-tax/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/tax/sales-tax${query}`)
  },
  listEligibleInvoices: <T = unknown>() =>
    apiGet<T>('/tax/sales-tax/eligible-invoices'),
  generate: <T = unknown>(invoiceId: string) =>
    apiPost<T>(`/tax/calculate-sale-tax/${encodeInvoiceId(invoiceId)}`, {})
}

// Tax Exemptions API — function: "tax"
export const taxExemptionsApi = {
  get: <T = unknown>(id: string) =>
    apiGet<T>(`/tax/exemption-certificates/${id}`),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/tax/exemption-certificates${query}`)
  },
  updateCertificate: <T = unknown>(taxExemptionId: string, data: unknown) =>
    apiPost<T>(`/tax/update-exemption-certificate/${taxExemptionId}`, data),
  updateTypeAndRegions: <T = unknown>(taxExemptionId: string, data: unknown) =>
    apiPost<T>(`/tax/update-exemption-type-and-regions/${taxExemptionId}`, data)
}

// Categories API — function: "category"
export const categoriesApi = {
  list: <T = unknown>() => apiGet<T>('/category'),
  get: <T = unknown>(id: string) => apiGet<T>(`/category/${id}`),
  create: <T = unknown>(data: unknown) => apiPost<T>('/category', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPut<T>(`/category/${id}`, data),
  delete: (id: string) => apiDelete(`/category/${id}`)
}

// Sectors API — function: "sector" (read-only backend)
export const sectorsApi = {
  list: <T = unknown>() => apiGet<T>('/sector'),
  get: <T = unknown>(id: string) => apiGet<T>(`/sector/${id}`),
  create: <T = unknown>(data: unknown) => apiPost<T>('/sector', data),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPut<T>(`/sector/${id}`, data),
  delete: (id: string) => apiDelete(`/sector/${id}`)
}

// Product Views API — uses product function routes
export const productViewsApi = {
  getCount: <T = unknown>(productId: string) =>
    apiGet<T>(`/product/${productId}/views/count`)
}

// Product Bids API — uses product function routes
export const productBidsApi = {
  list: <T = unknown>(productId: string) =>
    apiGet<T>(`/product/${productId}/bids`)
}

// Dashboard API — aggregated stats
export const dashboardApi = {
  getStats: <T = unknown>() => apiGet<T>('/helper/dashboard-stats'),
  getRecentActivity: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/helper/recent-activity${query}`)
  }
}

// Personal Details API — uses user function routes
export const personalDetailsApi = {
  get: <T = unknown>(contactId: string) =>
    apiGet<T>(`/user/personal-detail/${contactId}`),
  update: <T = unknown>(contactId: string, data: unknown) =>
    apiPatch<T>(`/user/personal-detail/${contactId}`, data),
  waiveBidderDeposit: <T = unknown>(contactId: string, status: boolean) =>
    apiPost<T>(`/user/waive-bidder-deposit/${contactId}`, {status})
}

// Bank Details API — uses user/adminuser function routes
export const bankDetailsApi = {
  get: <T = unknown>(contactId: string, accountId: string) =>
    apiGet<T>(`/user/bank-data/${contactId}/${accountId}`),
  getAdmin: <T = unknown>() => apiGet<T>('/adminuser/bank-account-details'),
  listPendingAdminChanges: <T = unknown>() =>
    apiGet<T>('/adminuser/bank-account-details/changes/pending'),
  updateAdmin: <T = unknown>(data: unknown) =>
    apiPatch<T>('/adminuser/bank-account-details', data),
  approveAdminChange: <T = unknown>(changeId: string) =>
    apiPost<T>(
      `/adminuser/bank-account-details/changes/${changeId}/approve`,
      {}
    ),
  rejectAdminChange: <T = unknown>(changeId: string) =>
    apiPost<T>(`/adminuser/bank-account-details/changes/${changeId}/reject`, {})
}

export const assetReportsApi = {
  getSettings: <T = unknown>() =>
    apiGet<T>('/adminuser/asset-reports/settings'),
  updateSettings: <T = unknown>(data: unknown) =>
    apiPatch<T>('/adminuser/asset-reports/settings', data),
  run: <T = unknown>() => apiPost<T>('/adminuser/asset-reports/run', {}),
  getActiveGenerationJob: <T = unknown>() =>
    apiGet<T>('/adminuser/asset-reports/generation-jobs/active'),
  listGenerationJobs: <T = unknown>(take = 12) =>
    apiGet<T>(`/adminuser/asset-reports/generation-jobs?take=${take}`),
  retryGenerationJob: <T = unknown>(jobId: string) =>
    apiPost<T>(`/adminuser/asset-reports/generation-jobs/${jobId}/retry`, {}),
  getGenerationJob: <T = unknown>(jobId: string) =>
    apiGet<T>(`/adminuser/asset-reports/generation-jobs/${jobId}`),
  runSingle: <T = unknown>(
    reportId: string,
    params?: Record<string, string>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiPost<T>(`/adminuser/asset-reports/${reportId}/run${query}`, {})
  },
  sendAll: <T = unknown>() =>
    apiPost<T>('/adminuser/asset-reports/send-all', {}),
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/adminuser/asset-reports${query}`)
  },
  get: <T = unknown>(reportId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/adminuser/asset-reports/${reportId}${query}`)
  },
  send: <T = unknown>(
    reportId: string,
    data?: unknown,
    params?: Record<string, string>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiPost<T>(
      `/adminuser/asset-reports/${reportId}/send${query}`,
      data || {}
    )
  },
  deleteReport: (
    reportId: string,
    reportTitle: string,
    params?: Record<string, string>
  ) => {
    const queryParams = new URLSearchParams(params)
    queryParams.set('reportTitle', reportTitle)
    return apiDelete(
      `/adminuser/asset-reports/${reportId}/report?${queryParams}`
    )
  }
}

export const outboundEmailPolicyApi = {
  get: <T = unknown>() => apiGet<T>('/adminuser/outbound-email-policy'),
  update: <T = unknown>(data: unknown) =>
    apiPatch<T>('/adminuser/outbound-email-policy', data)
}

// Collections API — generic Firestore-replacement queries
export const collectionsApi = {
  query: <T = unknown>(
    collectionName: string,
    params?: Record<string, string>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/helper/collection/${collectionName}${query}`)
  },
  getDocument: <T = unknown>(collectionName: string, docId: string) =>
    apiGet<T>(`/helper/collection/${collectionName}/${docId}`),
  queryByIds: <T = unknown>(
    collectionName: string,
    field: string,
    ids: string[]
  ) => apiPost<T>(`/helper/collection/${collectionName}/query`, {field, ids})
}

// Feedback API — uses user function routes
export const feedbackApi = {
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/user/feedback${query}`)
  },
  get: <T = unknown>(id: string) => apiGet<T>(`/user/feedback/${id}`)
}

// Agreements API — Zoho Sign plus Super Admin manual completion
export const agreementsApi = {
  list: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/zohoSign/agreements${query}`)
  },
  get: <T = unknown>(id: string) => apiGet<T>(`/zohoSign/agreements/${id}`),
  update: <T = unknown>(id: string, data: unknown) =>
    apiPut<T>(`/zohoSign/agreements/${id}`, data),
  pdf: (id: string, disposition: 'inline' | 'attachment' = 'inline') =>
    apiGetBlob(
      `/zohoSign/agreements/${encodeURIComponent(
        id
      )}/pdf?disposition=${disposition}`
    )
}

// TaxJar API — uses tax function routes
export const taxjarApi = {
  listCustomers: <T = unknown>(params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/tax/get-customer-list${query}`)
  },
  getCustomer: <T = unknown>(accountId: string) =>
    apiGet<T>(`/tax/view-customer/${accountId}`),
  createCustomer: <T = unknown>(accountId: string, data: unknown) =>
    apiPatch<T>(`/tax/create-customer/${accountId}`, data),
  updateCustomer: <T = unknown>(accountId: string, data: unknown) =>
    apiPatch<T>(`/tax/update-customer/${accountId}`, data),
  deleteCustomer: (accountId: string) =>
    apiDelete(`/tax/delete-customer/${accountId}`)
}

// Subcategories API — uses category function routes
export const subcategoriesApi = {
  create: <T = unknown>(categoryId: string, data: unknown) =>
    apiPost<T>(`/category/${categoryId}/subcategories`, data),
  update: <T = unknown>(
    categoryId: string,
    subcategoryId: string,
    data: unknown
  ) =>
    apiPut<T>(`/category/${categoryId}/subcategories/${subcategoryId}`, data),
  delete: (categoryId: string, subcategoryId: string) =>
    apiDelete(`/category/${categoryId}/subcategories/${subcategoryId}`)
}

// Chat API — function: "chat"
export const chatApi = {
  listConversations: <T = unknown>(
    contactId: string,
    params?: Record<string, string>
  ) => {
    const query = new URLSearchParams({contactId, ...params})
    return apiGet<T>(`/chat?${query}`)
  },
  getMessages: <T = unknown>(
    conversationId: string,
    params?: Record<string, string>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return apiGet<T>(`/chat/${conversationId}/messages${query}`)
  },
  streamMessages: <T = unknown>(
    conversationId: string,
    params: Record<string, string> | undefined,
    handlers: ApiStreamHandlers<T>
  ) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return openApiStream<T>(`/chat/${conversationId}/stream${query}`, handlers)
  },
  sendMessage: <T = unknown>(
    conversationId: string,
    data: {
      senderContactId: string
      previewSenderId?: string
      text: string
      messageType?: string
      attachmentUrl?: string
      attachments?: Array<{
        downloadURL: string
        name: string
        type?: string
      }>
    }
  ) => apiPost<T>(`/chat/${conversationId}/messages`, data),
  markSeen: <T = unknown>(conversationId: string, messageId: string) =>
    apiPut<T>(`/chat/${conversationId}/messages/${messageId}/seen`, {}),
  markRead: <T = unknown>(conversationId: string) =>
    apiPut<T>(`/chat/${conversationId}/read`, {}),
  deleteMessage: (conversationId: string, messageId: string) =>
    apiDelete(`/chat/${conversationId}/messages/${messageId}`),
  archive: (conversationId: string) => apiDelete(`/chat/${conversationId}`),
  unarchive: (conversationId: string) =>
    apiDelete(`/chat/${conversationId}?archive=false`),
  deleteChat: (conversationId: string) =>
    apiDelete(`/chat/${conversationId}?softDelete=true`),
  restoreChat: (conversationId: string) =>
    apiPut(`/chat/${conversationId}/restore`, {})
}
